import type { Translate } from './tileViewModel'

/**
 * The bet-history slip's words (B26, redrawn as a slip in B26d).
 *
 * A25 — THE EXTRACTOR IS LITERAL-ONLY. A key exists for `i18n:sync`, and
 * survives `i18n:cleanup`, only if the exact English sentence appears quoted as
 * the first argument of a `t()` call in a non-test source file. So every string
 * below is written out in full here, once, and nothing in this feature ever
 * feeds a computed key or a `Record` lookup through `t()`.
 *
 * ALMOST ALL OF IT IS BORROWED. The stake heading and the totals label are the
 * chip panel's; the three settled words and the void word are the flat row's;
 * the two market names are the board's own column headings; the Lightning
 * mark is the round card's eyebrow word; the six fact labels, the copy pair,
 * the round word, the replay link, the fairness opener and the empty state
 * are the bet-history panel's own — all already translated in sixteen
 * locales. Reusing them is what makes a Birdie entry read like the same panel
 * a player already knows.
 *
 * THREE are new, because nothing in the widget says them: the two remaining
 * column headings (the price a bet was taken at, and what it returned — the
 * sweepstakes locale words them as price and payout), and the "still open"
 * note under the totals label. Filled in all sixteen locales by
 * `scripts/birdieLocales.mjs` and gated by `birdieLocales.test.ts`.
 */
export const birdieBetHistoryCopy = (t: Translate) => ({
  /** The slip's column headings. The first column's is the bet count below. */
  stake: t('Stake'),
  odds: t('Odds'),
  returns: t('Return'),
  /** The totals row's label. */
  total: t('Total'),
  /** The verdict chip's four words, and a row's own state word. */
  won: t('Won'),
  lost: t('Lost'),
  pending: t('Pending'),
  /** A voided round or bet: its own word, never a loss. */
  cancelled: t('Cancelled'),
  /** The eyebrow's mark on a boosted round — the round card's own word. */
  lightning: t('Lightning Round'),
  /** The market names — the board's column headings, plus the round-type pair. */
  exactOrder: t('Exact order'),
  anyOrder: t('Any order'),
  /**
   * B36. Named individually rather than as one "Round type" lane: a history row has to say WHICH
   * side the player took, and unlike the other two markets the option is the market here — there
   * are only two of them and no third thing to disambiguate.
   */
  frostRound: t('Frost round'),
  fireRound: t('Fire round'),
  /** The unfolded bet's fact labels, every one of them the flat row's. */
  market: t('Market'),
  betId: t('Bet Id'),
  betTime: t('Bet Time'),
  betAmount: t('Bet Amount'),
  wonAmount: t('Won Amount'),
  multiplier: t('Multiplier'),
  /** The eyebrow's first word, beside the round number — the card's own. */
  round: t('Round'),
  copy: t('Copy'),
  copied: t('Copied!'),
  /** The round's deep link into the support tool — the pane's own wording. */
  viewReplay: t('View replay & verify ↗'),
  /** The round's fairness opener (B33) — the flat row's own button label. */
  provablyFair: t('Provably fair'),
  /** The panel's empty state, unchanged from the flat list. */
  empty: t('No bet history found')
})

/**
 * "3 bets" — TWO literal calls, never one computed key.
 *
 * The same shape `birdieChipsClampedCopy` uses for the board's clamp notice,
 * and for the same reason: the files are keyed by the English sentence, so a
 * singular and a plural are two keys and the extractor has to see both spelled
 * out. A locale with no count-dependent form simply translates them the same.
 */
export const birdieBetCountLabel = (t: Translate, count: number): string =>
  count === 1 ? t('{{count}} bet', { count }) : t('{{count}} bets', { count })

/**
 * "1 pending" — the note under the totals label when a settled-looking entry
 * still has bets running. ONE key: English says it the same way for one and
 * for many, and a locale that needs two forms phrases around it in its single
 * translation (the settled capsule's own precedent).
 */
export const birdiePendingNote = (t: Translate, count: number): string =>
  t('{{count}} pending', { count })
